import { CoreBatchAction } from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import type { AiResponse } from '@store/reducers/ai/types'
import { artBoardActions } from '@store/reducers/artboard'
import { managerActions } from '@store/reducers/manager/actions'
import {
  commitProjectRequestPure,
  saveBackupSnapshot,
} from '@storeOld/actionCreators'
import type { SagaIterator } from 'redux-saga'
import { put, select } from 'redux-saga/effects'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { ProjectOrigin } from '@/types'
import { AIExternalActions, AIInternalActions } from '../actions'

interface HandleAIResponseOptions {
  skipProjectActions?: boolean
}

export function* handleAIResponse(
  response: AiResponse,
  { skipProjectActions = false }: HandleAIResponseOptions = {}
): SagaIterator<any[]> {
  const { assistant_text, actions = [], remainingRequests } = response

  yield put(
    AIExternalActions.addMessage({
      message: {
        type: 'assistant',
        text: assistant_text,
      },
    })
  )

  if (typeof remainingRequests === 'number') {
    yield put(AIInternalActions.updateRemainingRequests(remainingRequests))
  }

  sendGtmEvent(
    actions.length > 0
      ? GtmTriggerName.AI_CHAT_RESPONSE_ACTION
      : GtmTriggerName.AI_CHAT_RESPONSE_NO_ACTION
  )

  const projectMutationEnabled = actions.length > 0 && !skipProjectActions

  if (projectMutationEnabled) {
    yield put(saveBackupSnapshot())
    yield put(artBoardActions.setAiGeneratingStatus({ status: true }))
  }

  for (const item of actions) {
    if (item.type === 'ai.showActions') {
      yield put(
        AIExternalActions.addMessage({
          message: {
            type: 'actions',
            buttons: 'main',
          },
        })
      )

      continue
    }

    if (skipProjectActions) {
      continue
    }

    yield put({
      type: item.type,
      payload: item.payload,
    })
  }

  if (projectMutationEnabled) {
    yield put(
      commitProjectRequestPure(
        CoreBatchAction({
          type: 'ai_generate_assistant',
          actions,
        })
      )
    )

    yield put(artBoardActions.setAiGeneratingStatus({ status: false }))

    const projectId = yield select(migrationProjectSelector.getProjectId)
    if (!projectId) {
      yield put(
        managerActions.saveProjectSaga({
          ignoreAuthenticated: true,
          origin: ProjectOrigin.AI,
        })
      )
    }
  }

  return actions
}
